/**
 * @description All states are expected to be expressed as strings.
 */
export type BaseState = string;

/**
 * @description Defines any possible state, while allowing explicit state
 * definitions to still be supported via TypeScript autocomplete.
 *
 * @see https://github.com/microsoft/TypeScript/issues/29729 for more.
 */
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export type AnyState = string & {};

/**
 * @description All events must have an explicit type.
 */
export type BaseEvent = { type: string };

/**
 * @description All events must have a corresponding payload shape.
 */
export type BaseEventLookup<T extends string> = Record<T, BaseEvent>;

/**
 * @description The context of the state machine is expected to be an object.
 */
export type BaseContext = Record<string, unknown>;

/**
 * @description ContextUpdates are invoked to update the context of the state machine in reaction to state transitions.
 * Please refrain from adding any side effects when implementing a ContextUpdate.
 */
export type ContextUpdate<Context extends BaseContext, Event extends BaseEvent> = (
  context: Context,
  event: Event,
) => Context;

/**
 * @description Guards are invoked before entering into a state to check if we can enter it.
 */
export type Guard<Context extends BaseContext, Event extends BaseEvent> = (
  context: Context,
  event: Event,
) => boolean;

/**
 * @description State guards are checked before the state machine decides to transition to that state
 * If the state guard returns false, the state will not be transitioned to.
 */
export type StateGuard = () => boolean;

/**
 * @description Executed when re-entering the same state
 */
export type OnReEnterEffect<Context extends BaseContext, Event extends BaseEvent> = (
  prev: Context,
  current: Context,
  producer: EventProducer<Event>,
) => void;

/**
 * Executed when entering a state.
 * @description Use this effect to setup a state.
 */
export type OnEnterEffect<Context extends BaseContext, Event extends BaseEvent> = (
  curr: Context,
  producer: EventProducer<Event>,
) => void;

/**
 * Executed when exiting a state.
 * @description Use this effect to cleanup a state.
 */
export type OnExitEffect<Context extends BaseContext, Event extends BaseEvent> = (
  prev: Context,
  current: Context,
  producer: EventProducer<Event>,
) => void;

/**
 * Executed whilst in a state.
 * @description Use this effect to trigger behavior whilst in a state.
 */
export type OnExecuteEffect<Context extends BaseContext, Event extends BaseEvent> = (
  context: Context,
  producer: EventProducer<Event>,
) => void;

/**
 * @description Effects are run upon entering, exiting, or whilst in a state.
 */
export type Effects<Context extends BaseContext, Event extends BaseEvent> = {
  /**
   * Executed when re-entering the same state
   */
  onReEnter: OnReEnterEffect<Context, Event>[];
  /**
   * Executed when exiting a state.
   * @description Use this effect to cleanup a state.
   */
  onExit: OnExitEffect<Context, Event>[];
  /**
   * Executed when entering a state.
   * @description Use this effect to setup a state.
   */
  onEnter: OnEnterEffect<Context, Event>[];
  /**
   * Executed whilst in a state.
   * @description Use this effect to trigger behavior whilst in a state.
   */
  onExecute: OnExecuteEffect<Context, Event>[];
};

export type Effect = 'onReEnter' | 'onExit' | 'onEnter' | 'onExecute';

/**
 * State commands are special commands handled by the state machine.
 * @description Use state commands to indicate which state you want to transition to in specific scenarios.
 */
export type StateCommand = 'done';

export type TransitionState<State extends BaseState> = {
  previous: State;
  current: State;
};

export type TransitionListener<
  Event extends BaseEvent,
  EventLookup extends BaseEventLookup<Event['type']>,
  Context extends BaseContext,
  State extends BaseState,
> = (
  event: EventLookup[Event['type']],
  context: Context,
  state: TransitionState<State>,
  config: Transition<any, any, any, any>,
) => void;

export type AsyncTransitionListener<
  Event extends BaseEvent,
  EventLookup extends BaseEventLookup<Event['type']>,
  Context extends BaseContext,
  State extends BaseState,
> = (
  event: EventLookup[Event['type']] | AsyncResolvedEvent<any> | AsyncRejectedEvent,
  context: Context,
  state: TransitionState<State>,
  config: Transition<any, any, any, any>,
) => void;

/**
 * Transitions indicate a potential transition from state A to state B.
 * @summary Use transitions to indicate rules for your state machine.
 */
export type Transition<
  State extends BaseState,
  Context extends BaseContext,
  EventLookup extends BaseEventLookup<EventKey>,
  EventKey extends Event['type'],
> = {
  /**
   * A label helpful to developers to know which transition was fired.
   */
  label?: string;
  /**
   * The target state for this transition.
   */
  state: State | StateCommand;
  /**
   * The conditions under which this transition should occur.
   */
  guard: Guard<Context, EventLookup[EventKey]>[];
  /**
   * The updates we want to make to the machine's context as part of this transition.
   */
  context: ContextUpdate<Context, EventLookup[EventKey]>[];
};

/**
 * @description Targets for event producers.
 * Note that these can be combined using bitwise OR - i.e. `Target.Listeners | Target.StateMachine`.
 */
export const enum Target {
  Listeners = 1 << 0,
  StateMachine = 1 << 1,
}

export const enum TransitionType {
  Normal = 1 << 0,
  AsyncResolved = 1 << 1,
  AsyncRejected = 1 << 2,
  All = Normal | AsyncResolved | AsyncRejected,
}

/**
 * @summary Event producers provide a way for state machines to produce events in effects.
 * @description Use this in combination with {@link Machine.onEvent} to listen to events
 * which are generated by a state machine.
 *
 * @param event - The event to produce.
 * @param target - The target for the event. This can be used to control the recipient of an event;
 *    note that it is possible to combine targets using bitwise OR.
 */
export type EventProducer<Event extends BaseEvent> = (
  event: Event,
  target?: Target,
) => void;

/**
 * @summary Event listeners provide a way to listen to events fired by a state machine (using an {@link EventProducer}).
 * @description Use this in combination with {@link EventProducer} to listen to events on a state machine.
 */
export type EventListener<Event extends BaseEvent> = (event: Event) => void;

/**
 * Base configuration of a state.
 * @description Use the state configuration to express the effects for this state.
 */
interface StateBaseConfig<
  State extends BaseState,
  Event extends BaseEvent,
  EventLookup extends BaseEventLookup<Event['type']>,
  Context extends BaseContext,
> {
  /**
   * The states the state machine can enter. These are child states which are evaluated dependent on
   */
  states?: Partial<Record<State, StateConfig<State, Event, EventLookup, Context>>>;
  /**
   * The effects we want to run on enter, exit and whilst in this state.
   */
  effects: Partial<Effects<Context, Event>>;
  /**
   * State guards which must be true before the state can be transitioned to
   */
  guards?: StateGuard[];
}

/**
 * Transition configuration of a state, indicating the transitions feasible from this state.
 * @description Use the state configuration to indicate what states the machine can transition to from this state.
 */
export interface StateTransitionConfig<
  State extends BaseState,
  Event extends BaseEvent,
  EventLookup extends BaseEventLookup<Event['type']>,
  Context extends BaseContext,
> extends StateBaseConfig<State, Event, EventLookup, Context> {
  /**
   * A list of transitions based on the events the state machine can ingest.
   */
  transitions: Partial<{
    [EventKey in Event['type']]: Transition<State, Context, EventLookup, EventKey>[];
  }>;
  /**
   * An opt-out flag which stops hierarchical traversal in the state machine
   * from child states.
   */
  trap?: boolean;
}

/**
 * Passthrough configuration of a state, indicating the eventless, immediate transitions available from this state.
 * @description Use the state configuration to indicate what states the machine can transition to without an event.
 */
export interface StatePassThroughConfig<
  State extends BaseState,
  Event extends BaseEvent,
  EventLookup extends BaseEventLookup<Event['type']>,
  Context extends BaseContext,
> extends StateBaseConfig<State, Event, EventLookup, Context> {
  /**
   * A list of transitions based on the events the state machine can ingest.
   */
  passthroughs: Transition<State, Context, EventLookup, Event['type']>[];
}

export type AsyncResolvedEventKey = 'async-resolved';
export type AsyncRejectedEventKey = 'async-rejected';

/**
 * An event that is dispatched when an async state is resolved.
 * @description Async resolved event.
 */
export interface AsyncResolvedEvent<TR = unknown> {
  /**
   * The type of the event.
   */
  type: AsyncResolvedEventKey;
  result: TR;
}

/**
 * An event that is dispatched when an async state is rejected.
 * @description Async rejected event.
 */
export interface AsyncRejectedEvent {
  /**
   * The type of the event.
   */
  type: AsyncRejectedEventKey;

  /**
   * The error that occurred.
   */
  error: Error;

  /**
   * The reason the async state was rejected.
   */
  reason: 'error' | 'timeout';
}

/**
 * A lookup of async events.
 */
export type AsyncEventLookup<TR = unknown> = {
  'async-resolved': AsyncResolvedEvent<TR>;
  'async-rejected': AsyncRejectedEvent;
};

/**
 * A transition that is triggered when an async state is resolved.
 * @description Async resolved transition.
 */
export type AsyncResolvedTransition<
  State extends BaseState,
  Context extends BaseContext,
  TR = unknown,
> = Transition<State, Context, AsyncEventLookup<TR>, AsyncResolvedEventKey>;

/**
 * A transition that is triggered when an async state is rejected.
 * @description Async rejected transition.
 */
export type AsyncRejectedTransition<
  State extends BaseState,
  Context extends BaseContext,
> = Transition<State, Context, AsyncEventLookup, AsyncRejectedEventKey>;

/**
 * Configuration of an async state.
 * @description Use the async state configuration to indicate which states this state can transition to.
 */
export interface AsyncStateConfig<
  State extends BaseState,
  Event extends BaseEvent,
  EventLookup extends BaseEventLookup<Event['type']>,
  Context extends BaseContext,
  TR = unknown,
> extends StateBaseConfig<State, Event, EventLookup, Context> {
  /**
   * The async action to perform upon entering this state.
   *
   * This is functionally similar to the `onEnter` side effect; but the state machine
   * will automatically trigger an async event {@link AsyncResolvedEvent} or {@link AsyncRejectedEvent}
   * based on the result of the async action.
   */
  asyncAction: (ctx: Context, signal: AbortSignal) => Promise<TR>;
  /**
   * The transitions to trigger when the async action is resolved.
   */
  resolvedTransitions: AsyncResolvedTransition<State, Context, TR>[];
  /**
   * The transitions to trigger when the async action is rejected.
   */
  rejectedTransitions: AsyncRejectedTransition<State, Context>[];
  /**
   * The timeout for the async action.
   */
  timeout?: number | ((ctx: Context) => number);
}

/**
 * Configuration of a state. Can define passthroughs or transitions only.
 * @description Use the state  configuration to indicate which states this state can transition to.
 */
export type StateConfig<
  State extends BaseState,
  Event extends BaseEvent,
  EventLookup extends BaseEventLookup<Event['type']>,
  Context extends BaseContext,
> =
  | StateTransitionConfig<State, Event, EventLookup, Context>
  | StatePassThroughConfig<State, Event, EventLookup, Context>
  | AsyncStateConfig<State, Event, EventLookup, Context>;

/**
 * Configuration of the state machine, indicating the states one can enter.
 * @description Use the state machine configuration to indicate which states the state machine can handle.
 */
export interface StateMachineConfig<
  State extends BaseState,
  Event extends BaseEvent,
  EventLookup extends BaseEventLookup<Event['type']>,
  Context extends BaseContext,
> {
  /**
   * The initial state of the state machine.
   */
  initial: State;
  /**
   * The states the state machine can enter.
   */
  states: Partial<Record<State, StateConfig<State, Event, EventLookup, Context>>>;
  /**
   * The context of the state machine.
   */
  context: Context;
  /**
   * A set of transitions that apply to all states within the state machine
   */
  globalTransitions?: Partial<{
    [EventKey in Event['type']]: Transition<State, Context, EventLookup, EventKey>[];
  }>;
}

/**
 * Configuration of the parallel state machine, indicating which sub-machines run in parallel
 * @description Use the parallel state machine configuration to indicate which machines are to be managed in parallel.
 */
export interface StateMachineParallelConfig<
  State extends BaseState,
  Event extends BaseEvent,
  EventLookup extends BaseEventLookup<Event['type']>,
  Context extends BaseContext,
> {
  /**
   * The sub-machines composing the parallel machine.
   */
  machines: Partial<
    Record<State, StateMachineConfig<State, Event, EventLookup, Context>>
  >;
}

/**
 * The consuming API contract of a state machine.
 */
export interface Machine<
  State extends BaseState,
  Event extends BaseEvent,
  EventLookup extends BaseEventLookup<Event['type']>,
  Context extends BaseContext,
> {
  /**
   * Feed an event into the state machine.
   * Results in the state machine transitioning to its next state, based on the configured transitions for this state + event combination.
   */
  send: (event: EventLookup[Event['type']]) => void;
  /**
   * Get the current state of the state machine.
   * @returns Current state of the state machine.
   */
  state: () => State;
  /**
   * Get the current context of the state machine.
   * @returns Current context of the state machine.
   */
  context: () => Context;
  /**
   * Executes all pending effects on the state machine.
   * @description Executes the `onExecute` of the current state.
   */
  execute: () => void;
  /**
   * Clear the machine back to its initial state.
   * @description Resets the machine to its initial state, including the extended context.
   */
  reset: () => void;
  /**
   * Destroy the machine.
   * @description Clears all memory held onto by the machine.
   */
  destroy: () => void;
  /**
   * Add a listener that fires on every state transition
   */
  onTransition(
    listener: AsyncTransitionListener<Event, EventLookup, Context, State>,
    type:
      | TransitionType.All
      | TransitionType.AsyncResolved
      | TransitionType.AsyncRejected,
  ): void;

  /**
   * Add a listener that fires on every state transition
   */
  onTransition(
    listener: TransitionListener<Event, EventLookup, Context, State>,
    type?: TransitionType.Normal,
  ): void;

  /**
   * Adds a listener when a 'final' state is reached.
   */
  onDone: (listener: VoidFunction) => void;
  /**
   * Adds a listener when events produced by a state machine (@see {@link EventProducer}) are fired.
   */
  onEvent: (listener: EventListener<Event>) => void;
  /**
   * Creates a machine based on the current machine definition, starting at a particular state.
   */
  from: (state: State, context?: Context) => Machine<State, Event, EventLookup, Context>;
  /**
   * Returns the config of the current state machine.
   */
  config: () => StateMachineConfig<State, Event, EventLookup, Context>;
  /**
   * Find the definition of a state.
   */
  lookup: (state: State) => StateConfig<State, Event, EventLookup, Context> | undefined;
}

/**
 * The consuming API contract of a state machine.
 */
export interface ParallelMachine<
  StateTree extends BaseState,
  State extends BaseState,
  Event extends BaseEvent,
  EventLookup extends BaseEventLookup<Event['type']>,
  Context extends BaseContext,
> extends Omit<
  Machine<State, Event, EventLookup, Context>,
  'state' | 'context' | 'onTransition' | 'onEvent' | 'onDone' | 'from' | 'config'
> {
  /**
   * Get the current state of each sub-machine in the state machine.
   * @returns Current state of each sub- machine.
   */
  state: () => Record<StateTree, State>;
  /**
   * Get the current context of the state machine.
   * @returns Current context of the state machine.
   */
  context: () => Record<StateTree, Context>;
  /**
   * Add a listener that fires on every state transition
   */
  onTransition: (
    state: StateTree,
    listener: TransitionListener<Event, EventLookup, Context, State>,
  ) => void;
  /**
   * Adds a listener when a 'final' state is reached.
   */
  onDone: (state: StateTree, listener: VoidFunction) => void;
}
